import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";

/**
 * 드롭다운 목록을 트리거 요소에 붙여 화면 좌표로 띄우기 위한 훅.
 *
 * 목록을 `position: absolute` 로 두면 조상 중 하나라도 `overflow: auto|hidden` 이면
 * 잘린다. 표는 `overflow-x-auto` 컨테이너 안에 있으므로 배차 등록처럼 행이 적은
 * 화면에서도 목록이 표 밖으로 나가지 못하고 스크롤해야 보이는 문제가 생긴다.
 *
 * 그래서 목록은 `document.body` 로 포털해 `position: fixed` 로 띄우고, 위치만
 * 트리거의 `getBoundingClientRect()` 로 계산한다. 스크롤·리사이즈 때 다시 계산하고,
 * 아래 공간이 부족하면 위로 뒤집는다.
 */
export interface AnchoredMenuStyle {
  position: "fixed";
  left: number;
  top: number;
  width: number;
  maxHeight: number;
  zIndex: number;
}

/** 목록과 트리거 사이 간격(px) */
const GAP = 4;
/** 뷰포트 가장자리 여백(px) */
const EDGE = 8;
/** 목록 최소 너비(px) — 표 셀이 좁아도 장소명 + 보조설명이 한 줄에 들어가게 한다 */
const MIN_WIDTH = 240;
/** 목록 최대 높이(px) */
const MAX_HEIGHT = 260;

export function useAnchoredMenu(open: boolean) {
  const anchorRef = useRef<HTMLElement | null>(null);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const [style, setStyle] = useState<AnchoredMenuStyle | null>(null);

  const reposition = useCallback(() => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();

    const spaceBelow = window.innerHeight - rect.bottom - GAP - EDGE;
    const spaceAbove = rect.top - GAP - EDGE;
    // 아래가 좁고 위가 더 넓으면 위로 뒤집는다.
    const flipUp = spaceBelow < 160 && spaceAbove > spaceBelow;
    const maxHeight = Math.max(120, Math.min(MAX_HEIGHT, flipUp ? spaceAbove : spaceBelow));

    const width = Math.max(rect.width, MIN_WIDTH);
    const left = Math.min(Math.max(EDGE, rect.left), window.innerWidth - width - EDGE);
    const top = flipUp ? rect.top - GAP - maxHeight : rect.bottom + GAP;

    setStyle({ position: "fixed", left, top, width, maxHeight, zIndex: 60 });
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setStyle(null);
      return;
    }
    reposition();
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    // 스크롤은 어느 조상에서 일어날지 모르므로 캡처 단계에서 전부 듣는다.
    const onScroll = () => reposition();
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, reposition]);

  /** 트리거와 목록 어느 쪽도 아닌 곳을 눌렀는지 판정한다(목록이 포털이라 필요). */
  const isOutside = useCallback((target: Node | null) => {
    if (!target) return true;
    if (anchorRef.current?.contains(target)) return false;
    if (menuRef.current?.contains(target)) return false;
    return true;
  }, []);

  return { anchorRef, menuRef, style, reposition, isOutside };
}
